import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    // וודא ש-marqueeItems[0] קיים ומכיל ילדים, כלומר יש תוכן שהמשתמש הוסיף
    if (!marqueeItems[0] || marqueeItems[0].children.length === 0) {
      console.warn("Marquee component: No content blocks found in 'marqueeItems'. Skipping initialization.");
      return;
    }

    // נקה קלונים קודמים כדי להתחיל ממצב נקי
    this.#cleanupClones();

    // בניית התוכן הפנימי של ה-marquee__content
    this.#buildInnerContent();

    // שכפל את כל ה-content (הכולל את המקורי ואת הקלונים) אל ה-wrapper
    this.#duplicateFullContent();

    this.#setSpeed();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;
    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => { this.#animation = null; },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();
    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => { this.#animation = null; },
    });
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const { marqueeItems, content } = this.refs;

    if (!marqueeItems[0] || marqueeItems[0].children.length === 0 || content.offsetWidth === 0) {
        return speedFactor; // ברירת מחדל
    }

    const marqueeWidth = this.offsetWidth; // רוחב רכיב המרקע עצמו
    const totalContentWidth = content.offsetWidth; // רוחב ה-content *לאחר* השכפולים הפנימיים

    // אנחנו רוצים שהאנימציה תשלים מעבר של רוחב ה-content כולו.
    // היחס בין הרוחב הכולל של התוכן לרוחב אזור התצוגה של המרקע
    // קובע כמה זמן ייקח לתוכן לעבור.
    // חלקי 2 כי ה-wrapper מכיל שני עותקים של ה-content (הכפול שלם)
    const speed = (totalContentWidth / marqueeWidth) * speedFactor / 2;

    // וודא שלא נחזיר מהירות שהיא 0 או שלילית, ומינימום כלשהו
    return Math.max(speedFactor * 0.5, speed); // מינימום מהירות כדי שלא יהיה איטי מדי
  }

  #handleResize = debounce(() => {
    this.#cleanupClones(); // נקה את הכל לפני בנייה מחדש
    this.#buildInnerContent(); // בנה את התוכן הפנימי מחדש
    this.#duplicateFullContent(); // שכפל את כל ה-content המעודכן
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();
    requestAnimationFrame(() => {
      for (const animation of animations) {
        // אם האנימציה הסתיימה או נעצרה, נתחיל אותה מחדש
        if (animation.playState !== 'running') {
            animation.currentTime = 0;
            animation.play();
        } else {
            // אם היא כבר רצה, פשוט נאפס את הזמן
            animation.currentTime = 0;
        }
      }
    });
  }

  /**
   * מנקה את כל האלמנטים שסומנו כקלונים.
   */
  #cleanupClones() {
    const { content, wrapper } = this.refs;

    // הסר קלונים של פריטי בלוק בתוך ה-content (ילדים של marqueeItems[0])
    // חשוב להתחיל מהסוף כדי לא לשבש את האינדקסים
    if (this.refs.marqueeItems[0]) {
      for (let j = this.refs.marqueeItems[0].children.length - 1; j >= 0; j--) {
          if (this.refs.marqueeItems[0].children[j].classList.contains('is-cloned-item')) {
              this.refs.marqueeItems[0].children[j].remove();
          }
      }
    }

    // הסר את הקלון המלא של ה-content מה-wrapper
    const existingFullContentClone = wrapper.querySelector('.marquee__content.is-full-content-clone');
    existingFullContentClone?.remove();
  }


  /**
   * בונה את התוכן הפנימי של ה-marquee__content על ידי שכפול הבלוקים
   * המקוריים (marqueeItems[0]'s children) מספיק פעמים.
   */
  #buildInnerContent() {
    const { marqueeItems } = this.refs;

    // אם אין פריט מקורי לשכפל, אין מה לבנות
    if (!marqueeItems[0] || marqueeItems[0].children.length === 0) return;

    const originalItemsContainer = marqueeItems[0];
    // אנחנו צריכים עותק טרי של הילדים המקוריים (לפני הוספת קלונים)
    const originalChildren = Array.from(originalItemsContainer.children).filter(child => !child.classList.contains('is-cloned-item'));

    // אם אין ילדים מקוריים אחרי הפילטור, משהו השתבש
    if (originalChildren.length === 0) {
        console.warn("Marquee component: No original children found in 'marqueeItems[0]' after filtering.");
        return;
    }

    // נקה קלונים קיימים בתוך originalItemsContainer (למקרה שה-cleanupClones לא עבד מספיק)
    for (let i = originalItemsContainer.children.length - 1; i >= 0; i--) {
        const child = originalItemsContainer.children[i];
        if (child.classList.contains('is-cloned-item')) {
            child.remove();
        }
    }

    // עכשיו נשכפל את הילדים המקוריים לתוך originalItemsContainer
    const numberOfSetsToEnsure = this.#calculateNumberOfSetsForInnerContent();
    const currentChildrenCount = originalChildren.length; // רק הילדים המקוריים שפילטרנו

    // כמה פריטים נוספים אנחנו צריכים מעבר למקוריים
    const itemsToClone = (numberOfSetsToEnsure * originalChildren.length) - currentChildrenCount;

    if (itemsToClone <= 0) return; // אין צורך להוסיף אם כבר יש מספיק

    for (let i = 0; i < itemsToClone; i++) {
        // קח את האלמנט הבא מהסט המקורי בלולאה
        const itemToClone = originalChildren[i % originalChildren.length];
        if (itemToClone) {
            const clone = itemToClone.cloneNode(true);
            clone.classList.add('is-cloned-item'); // סמן כקלון של פריט בודד
            originalItemsContainer.appendChild(clone);
        }
    }
  }


  /**
   * משכפל את כל ה-content (הכולל את הבלוקים המקוריים ואת הקלונים שלהם)
   * ומוסיף אותו ל-wrapper כדי ליצור לולאה חלקה.
   */
  #duplicateFullContent() {
    const { content, wrapper } = this.refs;

    // הסר קלון קודם אם קיים
    const existingFullContentClone = wrapper.querySelector('.marquee__content.is-full-content-clone');
    existingFullContentClone?.remove();

    const clone = /** @type {HTMLElement} */ (content.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref'); // חשוב להסיר את ה-ref
    clone.classList.add('is-full-content-clone'); // סמן את הקלון המלא
    wrapper.appendChild(clone);
  }

  /**
   * מחשב כמה סטים שלמים של התוכן המקורי נדרשים בתוך ה-marqueeItems[0]
   * כדי למנוע רווחים ריקים.
   * אנחנו רוצים שלפחות 2 עותקים של ה-marquee__content ימלאו את ה-wrapper.
   * כלומר, ה-marquee__content עצמו צריך להיות ארוך מספיק.
   */
  #calculateNumberOfSetsForInnerContent() {
    const { marqueeItems } = this.refs;
    const marqueeWidth = this.offsetWidth; // הרוחב המלא של רכיב המרקע

    if (!marqueeItems[0] || marqueeItems[0].children.length === 0) return 2; // ברירת מחדל

    const originalItemsContainer = marqueeItems[0];
    let originalBlocksWidth = 0;
    const gap = parseFloat(getComputedStyle(originalItemsContainer).gap) || 0;

    // חישוב רוחב של סט אחד של בלוקים מקוריים
    // רק הילדים שאינם קלונים
    const originalChildren = Array.from(originalItemsContainer.children).filter(child => !child.classList.contains('is-cloned-item'));

    if (originalChildren.length === 0) return 2; // למקרה שאין ילדים מקוריים

    originalChildren.forEach((child, index) => {
        originalBlocksWidth += child.offsetWidth;
        if (index < originalChildren.length - 1) { // הוסף gap בין הילדים המקוריים בלבד
            originalBlocksWidth += gap;
        }
    });

    if (originalBlocksWidth === 0) return 2;

    // אנחנו צריכים שהרוחב הכולל של ה-marqueeItems[0] (הסט המקורי + קלונים פנימיים)
    // יהיה לפחות פי X מרוחב המרקע.
    // ה-wrapper יכיל שני עותקים של ה-content, לכן ה-content צריך להיות לפחות
    // ברוחב המרקע כדי למנוע רווחים. נכפיל ב-1.5 כבטחון.
    const requiredWidthForContent = marqueeWidth * 1.5;

    const numberOfSets = Math.ceil(requiredWidthForContent / originalBlocksWidth);

    // נחזיר לפחות 2, כדי שיהיה מה לשכפל
    return Math.max(2, numberOfSets);
  }
}

// Define the animateValue function (ללא שינוי)
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  function animate(currentTime) {
    if (cancelled) return;
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;
    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }
  requestAnimationFrame(animate);

  return {
    get current() { return currentValue; },
    cancel() { cancelled = true; },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}